import { type JSX } from 'react';

import { Box, LinearProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';

const PROGRESS_HEIGHT = 2;
const BACKDROP_OPACITY = 0.5;
const Z_INDEX = 2;

export interface DialogSubmitOverlayProps {
  submitting: boolean;
}

/**
 * Top-anchored linear progress + semi-opaque backdrop rendered INSIDE a
 * dialog's content while a mutation is in flight. Form below stays
 * visible (50% opacity) so the user can still see what they submitted.
 * Pointer events captured so duplicate submits are impossible.
 *
 * The parent dialog must use `position: relative` (MUI Dialog content
 * already does) for the absolute positioning here to anchor correctly.
 */
export const DialogSubmitOverlay = ({
  submitting,
}: DialogSubmitOverlayProps): JSX.Element | null => {
  const { t } = useTranslation();
  if (!submitting) return null;
  return (
    <Box
      role="status"
      aria-busy="true"
      aria-label={t('submitting')}
      data-testid="dialog-submit-overlay"
      sx={{
        position: 'absolute',
        inset: 0,
        zIndex: Z_INDEX,
        display: 'flex',
        flexDirection: 'column',
        pointerEvents: 'auto',
      }}
    >
      <LinearProgress
        color="primary"
        sx={{ height: PROGRESS_HEIGHT, flexShrink: 0 }}
      />
      <Box
        sx={{
          flex: 1,
          bgcolor: 'rgba(255, 255, 255, ' + BACKDROP_OPACITY + ')',
        }}
      />
    </Box>
  );
};
